/**
 * Маппинг colorKeywords из Gamma-тем в CSS-цвета.
 *
 * Gamma возвращает свои темы с полем colorKeywords — массив человекочитаемых
 * названий цветов ('Green', 'dark', 'warm', ...). Картинок-превью у тем нет,
 * поэтому на фронте собираем визуальное представление из этих ключей: градиент
 * из первых двух цветов.
 *
 * Сопоставление захардкожено. Ключи — lowercase; lookup case-insensitive.
 */

const COLOR_KEYWORD_MAP: Record<string, string> = {
	green: '#22c55e',
	'lime green': '#84cc16',
	lime: '#84cc16',
	'neon green': '#4ade80',
	emerald: '#10b981',
	forest: '#166534',
	blue: '#3b82f6',
	navy: '#1e3a5a',
	'light blue': '#93c5fd',
	sky: '#0ea5e9',
	indigo: '#6366f1',
	red: '#ef4444',
	coral: '#f87171',
	crimson: '#dc2626',
	burgundy: '#7f1d1d',
	purple: '#a855f7',
	lavender: '#c4b5fd',
	violet: '#8b5cf6',
	gold: '#eab308',
	yellow: '#facc15',
	amber: '#f59e0b',
	mustard: '#ca8a04',
	orange: '#f97316',
	peach: '#fdba74',
	terracotta: '#c2410c',
	pink: '#ec4899',
	rose: '#fb7185',
	blush: '#fda4af',
	magenta: '#d946ef',
	fuchsia: '#d946ef',
	dark: '#1f2937',
	black: '#111827',
	charcoal: '#374151',
	onyx: '#0f172a',
	light: '#f9fafb',
	white: '#ffffff',
	cream: '#fef3c7',
	ivory: '#fefce8',
	gray: '#9ca3af',
	grey: '#9ca3af',
	silver: '#d1d5db',
	slate: '#64748b',
	teal: '#14b8a6',
	cyan: '#06b6d4',
	turquoise: '#2dd4bf',
	aqua: '#22d3ee',
	brown: '#92400e',
	chocolate: '#78350f',
	warm: '#b45309',
	cool: '#0369a1',
	mint: '#6ee7b7',
	sage: '#84cc16',
	olive: '#65a30d',
	beige: '#e7dcc9',
	tan: '#d4a574',
}

const FALLBACK_COLOR = '#6b7280'

/**
 * Возвращает первые N CSS-цветов из colorKeywords (по умолчанию 2).
 * Неизвестные ключи пропускает. Если не нашлось ни одного — отдаёт [fallback].
 */
export function getThemeColors(
	colorKeywords: string[] | undefined,
	max: number = 2,
): string[] {
	if (!colorKeywords || colorKeywords.length === 0) return [FALLBACK_COLOR]

	const resolved: string[] = []
	for (const kw of colorKeywords) {
		if (resolved.length >= max) break
		const color = COLOR_KEYWORD_MAP[String(kw).toLowerCase().trim()]
		if (color) resolved.push(color)
	}
	if (resolved.length === 0) return [FALLBACK_COLOR]
	return resolved
}

/**
 * Готовый CSS-background для карточки темы — линейный градиент 135°
 * из первых двух цветов, или solid при наличии только одного.
 */
export function getThemeGradient(colorKeywords: string[] | undefined): string {
	const colors = getThemeColors(colorKeywords, 2)
	if (colors.length < 2) return colors[0]
	return `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`
}

/**
 * true — если средняя яркость градиента достаточно тёмная, чтобы белый текст
 * поверх был читаем. Грубо: первый цвет — светлый → используем тёмный текст.
 */
export function isDarkTheme(colorKeywords: string[] | undefined): boolean {
	const [first] = getThemeColors(colorKeywords, 1)
	// hex → RGB → luminance
	const hex = first.replace('#', '')
	if (hex.length !== 6) return true
	const r = parseInt(hex.slice(0, 2), 16)
	const g = parseInt(hex.slice(2, 4), 16)
	const b = parseInt(hex.slice(4, 6), 16)
	const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255
	return luma < 0.6
}
